import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bin,
  Chip,
  EditIcon,
  Export,
  InfoCard,
  InfoCardDetail,
  IOption,
  MenuIcon,
  Typography,
  ViewIcon,
} from '@weareopenr/component-library';
import { IMenuSet } from 'src/api/getMenuSets';
import { findImageUrl } from 'src/hooks/useMenu/formatMenu/formatMenu';
import { useDeleteMenuSetPrompt } from 'src/hooks/useDeleteMenuSetPrompt';
import { DeleteEntityPrompts } from 'src/components/DeleteEntityPrompts';
import { SpinnerIcon } from 'src/components/SpinnerIcon';
import { Routes } from 'src/routes';
import { IMenuSetLocationState } from 'src/pages/MenuSet/MenuSet';
import styles from './MenuSetCard.module.scss';

export interface MenuSetCardProps {
  menuSet: IMenuSet;
  rangesEnabled: boolean;
  onPublish: () => void;
}

export const MenuSetCard = ({ menuSet, rangesEnabled, onPublish }: MenuSetCardProps) => {
  const navigate = useNavigate();

  const deletePrompt = useDeleteMenuSetPrompt({
    menuSetId: menuSet.menu_set_id,
    menuSetName: menuSet.name,
    genericErrorMessage: 'We have been unable to complete your request. Please try again',
  });

  const titleText = menuSet.consumer_name ? `${menuSet.name} (${menuSet.consumer_name})` : menuSet.name;

  const title: ReactNode = (
    <>
      <span className={styles['menu-set-name']}>{menuSet.name}</span>
      {menuSet.consumer_name && <span className={styles['menu-set-consumer-name']}> ({menuSet.consumer_name})</span>}
    </>
  );

  const getImage = (): ReactNode => {
    const imageUrl = findImageUrl(menuSet.assets);
    if (imageUrl) return <img src={imageUrl} alt={menuSet.name} />;

    return (
      <div className={styles['image-placeholder']}>
        <MenuIcon size="xlarge" />
      </div>
    );
  };

  const details: InfoCardDetail[] = [
    { text: 'Menus used', detail: String(menuSet.menus.length) },
    { text: 'Total menu sections', detail: String(menuSet.sections.length) },
  ];

  const getFootnote = () => {
    if (!rangesEnabled) return null;
    if (menuSet.ranges.length === 0) return <Typography size="xsmall">No Ranges</Typography>;

    return menuSet.ranges.map((range) => (
      <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
    ));
  };

  const menuSetPath = `${Routes.MENU_SETS}/${encodeURIComponent(menuSet.menu_set_id)}`;

  // The details tray edits the menu set shown behind it, so it opens on the details page rather than here.
  const openDetailsTray = () => {
    const state: IMenuSetLocationState = { openDetailsTray: true };
    navigate(menuSetPath, { state });
  };

  const ellipsisOptions: IOption[] = [
    {
      id: 'view',
      name: 'View',
      icon: ViewIcon,
      onOptionClick: () => navigate(menuSetPath),
    },
    {
      id: 'edit-details',
      name: 'Edit',
      icon: EditIcon,
      onOptionClick: openDetailsTray,
    },
    { id: 'divider-edit', isDivider: true },
    {
      id: 'publish',
      name: 'Publish',
      icon: Export,
      onOptionClick: onPublish,
    },
    { id: 'divider-publish', isDivider: true },
    {
      id: 'delete',
      name: 'Delete',
      icon: deletePrompt.isCheckingCanDelete ? SpinnerIcon : Bin,
      hoverColour: 'tomato',
      isDisabled: deletePrompt.isCheckingCanDelete,
      persistMenuOnClick: true,
      onOptionClick: deletePrompt.openDeletePrompt,
      dataTestId: 'open-remove-prompt',
    },
  ];

  return (
    <div>
      <InfoCard
        label="Menu Set"
        labelColour="grape"
        title={title}
        titleTooltip={titleText}
        image={getImage()}
        size="medium"
        border="solid"
        details={details}
        footnote={getFootnote()}
        ellipsisOptions={ellipsisOptions}
        onClick={() => navigate(menuSetPath)}
      />
      <DeleteEntityPrompts
        entityLabel="menu set"
        entityName={menuSet.name}
        unaffectedItems="menu"
        deletePrompt={deletePrompt}
      />
    </div>
  );
};

export default MenuSetCard;
